import { Spinner } from "@blueprintjs/core";
import {
    ColumnResizedEvent,
    FilterChangedEvent,
    IRowNode
} from "ag-grid-community";
import { AgGridReact, AgGridReactProps } from "ag-grid-react";
import classNames from "classnames";
import {
    segmentTrackScenarioTableColumnFiltered,
    segmentTrackTableColumnResized
} from "in_queue/analytics";
import { forwardRef, Ref, useCallback, useRef } from "react";
import { Loading } from "types/loadingType";
import { AgGridGroupHeaderRenderer } from "./AgGridGroupHeaderRow";
import css from "./BaseAgGrid.module.scss";
import { RowGroup, rowGroupingGridProps } from "./rowGrouping";

export interface BaseAgGridProps<TData extends object>
    extends AgGridReactProps<TData> {
    loadingData: Loading<TData[]>;
    /**
     * One of:
     *  - undefined → 500 px (legacy default).
     *  - a number → that many pixels.
     *  - "auto" → ag-grid's `autoHeight` domLayout; grid grows to fit all
     *    rows and the parent scroll container does the scrolling.
     */
    height?: "auto" | number;
    /**
     * When true, the column header is pinned to the top of the nearest
     * scroll ancestor (`.app-layout` in the IQ shell) via
     * `position: sticky`. Implicitly forces `domLayout="autoHeight"`
     * so the grid renders all rows without an internal scrollbar — the
     * page scrolls instead, and the sticky header keeps the column
     * names visible as the user scrolls through the rows.
     *
     * Used by the v2 In-Queue tables to drop the legacy 500 px box
     * with its nested scrollbar.
     */
    stickyHeader?: boolean;
    /**
     * Whether each row should show up as selectable. Makes cursor show as pointer
     * for each row, and disables ability to focus cells. Should be used in conjunction
     * with an onRowClicked event to navigate to another page.
     */
    selectableRows?: boolean;
    /**
     * When set, user-driven column resizes and column-header filtering on this
     * grid are tracked in analytics under this table name. Calling tables only
     * provide their name — they don't wire up individual analytics events.
     */
    analyticsTableName?: string;
    className?: string;
    /**
     * Receives this grid's outer wrapper element, so we can scroll to the top of the table
     * on any table actions.
     */
    wrapperRef?: React.MutableRefObject<HTMLDivElement | null>;
    /**
     * Splits the rows into groups, each introduced by a full-width header
     * band, and keeps the groups in the order declared — so a column sort
     * orders rows *within* a group instead of across the whole table.
     */
    rowGroup?: RowGroup<TData>;
    rowFilter?: (row: TData) => boolean;
}

const BaseAgGridInner = <TData extends object>(
    props: BaseAgGridProps<TData>,
    ref?: Ref<AgGridReact<TData>>
): React.ReactElement => {
    const {
        analyticsTableName,
        className,
        onColumnResized,
        onFilterChanged,
        onPaginationChanged: onPaginationChangedProp,
        wrapperRef: callerWrapperRef,
        rowGroup,
        rowFilter,
        ...agGridProps
    } = props;

    const isExternalFilterPresent = useCallback(
        () => rowFilter !== undefined,
        [rowFilter]
    );
    const doesExternalFilterPass = useCallback(
        (node: IRowNode<TData>) =>
            node.data !== undefined && rowFilter ? rowFilter(node.data) : true,
        [rowFilter]
    );
    const rowFilterProps =
        rowFilter === undefined
            ? undefined
            : { isExternalFilterPresent, doesExternalFilterPass };

    // Ref to the AG Grid wrapper so we can scroll it back to the top of
    // the page on pagination page changes and on (re)mount.
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const setWrapper = (node: HTMLDivElement | null) => {
        wrapperRef.current = node;
        if (callerWrapperRef !== undefined) {
            callerWrapperRef.current = node;
        }
    };
    // Skip the first onPaginationChanged event AG Grid fires when it
    // initially mounts (newPage / newPageSize are both true on init).
    const isFirstPaginationEvent = useRef(true);

    // `stickyHeader` implies autoHeight — the grid grows to fit content
    // and the page scrolls it. With autoHeight, AG Grid renders every
    // row in the DOM (no virtualization), so we ALSO opt the grid into
    // pagination here to bound the rendered row count at the caller-
    // provided `paginationPageSize` (default 500). This keeps the page
    // performant on heavy tables (constraints, allocated upgrades) that
    // commonly exceed several thousand rows in production.
    const useAutoHeight = props.stickyHeader || props.height === "auto";
    const divHeight = useAutoHeight
        ? undefined
        : props.height === undefined
        ? 500
        : props.height;
    const maybeRowStyle = props.selectableRows
        ? { cursor: "pointer" }
        : undefined;

    // Track only finished, user-driven resizes (drag handle). Ignores the
    // intermediate events fired during a drag and programmatic width changes
    // (autosize / flex / api / sizeColumnsToFit).
    const handleColumnResized = (event: ColumnResizedEvent<TData>) => {
        if (
            analyticsTableName !== undefined &&
            event.finished &&
            event.source === "uiColumnResized" &&
            event.column
        ) {
            segmentTrackTableColumnResized({
                tableName: analyticsTableName,
                columnId: event.column.getColId(),
                newWidth: event.column.getActualWidth()
            });
        }
        onColumnResized?.(event);
    };

    // Track only user-initiated column-header filtering. Ignores the quick
    // filter search box and programmatic filter changes (e.g. default filters
    // applied on load) via the event source.
    const handleFilterChanged = (event: FilterChangedEvent<TData>) => {
        if (
            analyticsTableName !== undefined &&
            event.source === "columnFilter"
        ) {
            const filteredColumns = Object.keys(event.api.getFilterModel());
            segmentTrackScenarioTableColumnFiltered({
                tableName: analyticsTableName,
                filteredColumns: filteredColumns.join(","),
                numActiveFilters: filteredColumns.length
            });
        }
        onFilterChanged?.(event);
    };

    const handlePaginationChanged: AgGridReactProps<TData>["onPaginationChanged"] =
        (event) => {
            // Only react after the first event (which fires on mount).
            // Then, if the active page actually changed, scroll the
            // grid wrapper back to the top of the page scroll container
            // so the user lands on row 1 of the new page. The
            // wrapper's `scroll-margin-top` lifts it BELOW the sticky
            // stack (tabs + ribbon + ag-header) on the scenario page.
            if (isFirstPaginationEvent.current) {
                isFirstPaginationEvent.current = false;
            } else if (event.newPage && wrapperRef.current) {
                // Only scroll when the wrapper top is above the
                // viewport — i.e. the user already scrolled past row 1
                // of the previous page. If the wrapper is already
                // visible (small table fitting on screen, user at top
                // of page) we don't want to yank the layout. Default
                // `scrollIntoView` behavior is `auto` (instant jump).
                if (wrapperRef.current.getBoundingClientRect().top < 0) {
                    wrapperRef.current.scrollIntoView({ block: "start" });
                }
            }
            onPaginationChangedProp?.(event);
        };

    const wrapperClassName = classNames(
        "ag-theme-balham",
        { [css["sticky-header"]]: props.stickyHeader },
        className
    );

    // v2 sticky-header tables get pagination at 50 rows per page
    // (default — caller can still override via `paginationPageSize` in
    // `agGridProps`). The page-size selector is hidden because the
    // prototype wants a single fixed page size for now. A bounded page
    // keeps the DOM small enough that AG Grid's lack of in-page row
    // virtualization in `autoHeight` mode is not a performance concern.
    const paginationProps = props.stickyHeader
        ? {
              pagination: true,
              paginationPageSize: 50,
              paginationPageSizeSelector: false as const
          }
        : undefined;

    const rows =
        props.loadingData === "loading" ? undefined : props.loadingData;

    // When `rowGroup` is set these replace rowData, columnDefs, and the
    // per-row callbacks with versions that account for the group header
    // rows
    const groupingProps =
        rowGroup === undefined || rows === undefined
            ? undefined
            : rowGroupingGridProps({
                  rowGroup,
                  rows,
                  props: {
                      ...agGridProps,
                      fullWidthCellRenderer: AgGridGroupHeaderRenderer
                  }
              });

    return (
        <div
            ref={setWrapper}
            className={wrapperClassName}
            style={{ height: divHeight }}
        >
            <AgGridReact<TData>
                ref={ref}
                rowData={rows}
                loadingOverlayComponent={Spinner}
                animateRows={false}
                enableCellTextSelection={true}
                tooltipShowDelay={500}
                defaultColDef={{ useValueFormatterForExport: false }}
                domLayout={useAutoHeight ? "autoHeight" : "normal"}
                rowStyle={maybeRowStyle}
                suppressCellFocus={props.selectableRows}
                {...paginationProps}
                {...agGridProps}
                {...groupingProps}
                onColumnResized={handleColumnResized}
                onFilterChanged={handleFilterChanged}
                onPaginationChanged={handlePaginationChanged}
                {...rowFilterProps}
            />
        </div>
    );
};

// These type shenanigans are necessary to support having a generic type TData
// *and* a forwarded ref. See https://fettblog.eu/typescript-react-generic-forward-refs/.
export const BaseAgGrid = forwardRef(BaseAgGridInner) as <TData extends object>(
    props: BaseAgGridProps<TData> & {
        ref?: React.ForwardedRef<AgGridReact<TData>>;
    }
) => ReturnType<typeof BaseAgGridInner>;
